import * as Ariakit from '@ariakit/react';
import { SelectRenderer } from '@ariakit/react-core/select/select-renderer';
import type { SelectRendererItem } from '@ariakit/react-core/select/select-renderer';
import { startTransition, useEffect, useState } from 'react';
import { styled } from 'styled-components';

import { OptionType } from './types';
import { Text } from '../../Text';
import { MenuCloseIcon } from '../../../assets/icons/MenuClose';
import { MagnifyingGlassIcon } from '../../../assets/icons/MagnifyingGlass';
import { ErrorMessage } from '../ErrorMessage';
import { SelectHeader, SelectItemContent, SelectWrapper } from './CommonSelectComponents';
import { unsetButtonStyle } from '../../../utils/unsetButtonStyle';
import { CommonSelectProps } from './types/CommonSelectProps';
import {
  SearchResultsFooter,
  SelectItemStyle,
  SelectListStyle,
  StyledSearchInput,
  StyledSelectPopover,
} from './Select';

export type SearchSelectProps = CommonSelectProps & {
  searchPlaceholder?: string;
  searchResetText?: string;
  // The virtualized renderer needs the height of the items
  itemsHaveDescription?: boolean;
};

// TODO: Use select groups to sort out favorites and non-multi options
function groupItems(items: OptionType[], favorites: OptionType[]) {
  return [
    {
      id: 'favorite-items',
      label: 'favorite items',
      // itemSize: 40,
      // paddingStart: 44,
      items: favorites,
    },
    {
      id: 'ohter-items',
      label: 'other items',
      // itemSize: 40,
      // paddingStart: 44,
      items,
    },
  ] satisfies SelectRendererItem[];
}

// Select with search
export const SearchSelect = ({
  value,
  setValue,
  options,
  label,
  hasError,
  disabled,
  errorMessage,
  errorVariant,
  testId,
  onToggleFavorite,
  searchResetText = 'Rensa sökning',
  showFavorites,
  searchPlaceholder = 'Sök...',
  itemsHaveDescription,
  hideLabel = false,
  hideDescription = false,
}: SearchSelectProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [matches, setMatches] = useState(options);
  const multiple = Array.isArray(value);

  useEffect(() => {
    // Create a timer each time searchValue (input) changes
    const debounceTimer = setTimeout(() => {
      if (searchValue.length >= 2 || searchValue === '') setDebouncedSearchValue(searchValue);
    }, 200);
    // Clear timeount when the hook "dies", i.e. when searchValue changes,
    // the old timer is cleared before the new version of the hook is run and a new timer is created
    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchValue]);

  useEffect(() => {
    startTransition(() => {
      if (debouncedSearchValue) {
        const searchLower = debouncedSearchValue.toLowerCase();

        const filteredOptions = options.filter(e => {
          const nameMatch = e.name.toLowerCase().includes(searchLower);
          if (nameMatch) return true;
          const descriptionMatch = e.description?.toLowerCase().includes(searchLower);
          return descriptionMatch;
        });
        setMatches(filteredOptions);
      } else {
        setMatches(options);
      }
    });
  }, [options, debouncedSearchValue]);

  const combobox = Ariakit.useComboboxStore({
    resetValueOnHide: true,
    value: searchValue,
    setValue: setSearchValue,
  });
  const selectStore = Ariakit.useSelectStore({
    combobox,
    items: options,
    value,
    setValue,
  });

  return (
    <SelectWrapper>
      <Ariakit.SelectLabel hidden={hideLabel} store={selectStore} style={{ marginBottom: '10px' }}>
        <Text color="black70"> {label} </Text>
      </Ariakit.SelectLabel>
      <SelectHeader
        options={options}
        label={label}
        selectStore={selectStore}
        disabled={disabled}
        hasError={hasError}
        testId={testId}
      />
      <StyledSelectPopover store={selectStore} sameWidth flip={false} fixed={true}>
        <SelectSearchInput searchPlaceholder={searchPlaceholder} comboboxStore={combobox} searchValue={searchValue} />
        <StyledComboboxList store={combobox}>
          {/* https://github.com/ariakit/ariakit/issues/3451 Combobox + Select is slow in performance,
          so using virtual selectStore with SelecteRenderer as per recommended in this github thread */}
          <SelectRenderer store={selectStore} items={matches} itemSize={itemsHaveDescription ? 62 : 50} overscan={8}>
            {({
              id,
              name,
              bold,
              description,
              favorite,
              icon,
              isDisabled,
              noMultiSelect,
              ...item
            }: OptionType & SelectRendererItem) => (
              <StyledComboboxItem
                key={id}
                {...item}
                data-testid={`test-option-${id}`}
                disabled={isDisabled}
                render={<Ariakit.SelectItem value={id} />}
                $itemsHaveDescription={itemsHaveDescription}
              >
                <SelectItemContent
                  option={{ id, name, bold, description, favorite, icon, isDisabled, noMultiSelect }}
                  multiple={multiple}
                  hideDescription={hideDescription}
                />
              </StyledComboboxItem>
            )}
          </SelectRenderer>
        </StyledComboboxList>
        {searchValue && (
          <ResultFooter
            comboboxStore={combobox}
            resultNumber={matches.length}
            totalNumber={options.length}
            searchResetText={searchResetText}
          />
        )}
      </StyledSelectPopover>
      {hasError && !disabled && (
        <ErrorMessage testId={`${testId}-error`} variant={errorVariant}>
          {errorMessage}
        </ErrorMessage>
      )}
    </SelectWrapper>
  );
};

const ResultFooter = ({
  searchResetText,
  resultNumber,
  totalNumber,
  comboboxStore,
}: Pick<SearchSelectProps, 'searchResetText'> & {
  resultNumber: number;
  totalNumber: number;
  comboboxStore: Ariakit.ComboboxStore;
}) => {
  return (
    <SearchResultsFooter>
      <Text fontWeight="semibold">{`${resultNumber} av ${totalNumber}`}</Text>
      {searchResetText && (
        <Ariakit.ComboboxCancel
          store={comboboxStore}
          render={
            <ClearButton>
              <Text color="foraBlue">{searchResetText}</Text>
            </ClearButton>
          }
        />
      )}
    </SearchResultsFooter>
  );
};

const SelectSearchInput = ({
  searchPlaceholder,
  searchValue,
  comboboxStore,
}: Pick<SearchSelectProps, 'searchPlaceholder'> & {
  searchValue: string;
  comboboxStore: Ariakit.ComboboxStore;
}) => {
  return (
    <>
      <SearchIconWrapper>
        {searchValue ? (
          <Ariakit.ComboboxCancel
            store={comboboxStore}
            render={
              <NoStyleButton>
                <MenuCloseIcon color="black" width={14} />
              </NoStyleButton>
            }
          />
        ) : (
          <MagnifyingGlassIcon color="black" width={14} />
        )}
      </SearchIconWrapper>
      <StyledSearchInput
        store={comboboxStore}
        autoSelect
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
      />
    </>
  );
};

const NoStyleButton = styled.button`
  ${unsetButtonStyle}
  cursor: pointer;
`;

const ClearButton = styled.button`
  ${unsetButtonStyle}
  cursor: pointer;
  :hover {
    p {
      text-decoration: underline;
    }
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  right: 18px;
  top: 0;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledComboboxItem = styled(Ariakit.ComboboxItem)<{ $itemsHaveDescription?: boolean }>`
  ${SelectItemStyle}

  height: ${({ $itemsHaveDescription }) => ($itemsHaveDescription ? 62 : 50)}px;
  // Ellipsis instead of text wrap since height needs to be fixed in virtualized renderer
  p {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;

const StyledComboboxList = styled(Ariakit.ComboboxList)`
  ${SelectListStyle}
`;
